// ============================================================================
// Document design / layout configuration.
//
// The "Diseño" tab in Personalización lets the account owner choose, *per
// document type* (Factura / Albarán / Presupuesto), a visual template plus
// which blocks of data are printed. A common use case: a presupuesto or
// albarán that hides the client's CIF / name / address.
//
// The whole config is stored as one JSONB blob on the company profile
// (profiles.document_design — see backend/add_document_design.sql). The PDF
// renderers read it from the owner's profile via <DocumentTemplate>.
// ============================================================================

import { DEFAULT_UI_THEME, isUiTheme } from "./uiTheme";

export const TEMPLATES = [
  { id: 'clasica', label: 'Clásica', desc: 'Logo a la izquierda, líneas marcadas.' },
  { id: 'moderna', label: 'Moderna', desc: 'Cabecera con banda de color.' },
  { id: 'minimalista', label: 'Minimalista', desc: 'Líneas finas, mucho espacio.' },
  { id: 'tpv', label: 'Comercio (TPV)', desc: 'Estilo ticket: columna de artículo, IVA por línea y condiciones de pago.' },
  { id: 'u26', label: 'U26', desc: 'Ticket de comercio con descripción de artículo grande y resumen de IVA (Base, Cuota, REQ.IVA, IBAE).' },
  { id: 'ks', label: 'KS', desc: 'Factura de comercio con precios con IVA incluido por línea, Venta Nº y resumen de IVA fijo (4%, 10%, 21%).' },
  { id: 'ryt', label: 'RYT', desc: 'Como KS pero sin columna de artículo: una línea por artículo, columna de descuento y filas compactas.' },
  { id: 'super', label: 'Supermercado', desc: 'Título grande, caja de cliente gris oscuro y tabla de artículos con cabecera negra.' },
];

export const FONTS = {
  'Sans-serif': 'ui-sans-serif, system-ui, -apple-system, "Segoe UI", Roboto, Arial, sans-serif',
  'Serif': 'Georgia, "Times New Roman", serif',
  'Monospace': 'ui-monospace, "Courier New", monospace',
};

export const COLORS = ['#1a4f8b', '#2a75d3', '#fca311', '#e0920f', '#28a745', '#dc3545', '#6c757d', '#1d2d3d'];

// Article-row type sizes (global). `scale` multiplies each template's natural
// items-table font size, so "Normal" reproduces the current look exactly.
export const ITEM_FONT_SIZES = [
  { key: 'small', label: 'Pequeño', scale: 0.85 },
  { key: 'normal', label: 'Normal', scale: 1 },
  { key: 'large', label: 'Grande', scale: 1.15 },
  { key: 'xlarge', label: 'Muy grande', scale: 1.3 },
];

// Resolve a stored item_font_scale key to its multiplier (defaults to 1).
export function itemFontScaleValue(key) {
  return (ITEM_FONT_SIZES.find((s) => s.key === key) || { scale: 1 }).scale;
}

// Toggleable blocks. `clientBlock` is a master switch for the whole CLIENTE box;
// when off, the individual client rows are irrelevant.
export const FIELD_DEFS = [
  { key: 'logo', label: 'Logo de la empresa' },
  { key: 'companyBlock', label: 'Bloque de tu empresa (nombre y datos)', master: true },
  { key: 'companyTaxId', label: 'CIF/NIF de tu empresa' },
  { key: 'companyAddress', label: 'Dirección de tu empresa' },
  { key: 'companyContact', label: 'Teléfono / email de tu empresa' },
  { key: 'clientBlock', label: 'Bloque del cliente (nombre y datos)', master: true },
  { key: 'clientTaxId', label: 'CIF/DNI del cliente' },
  { key: 'clientAddress', label: 'Dirección del cliente' },
  { key: 'clientContact', label: 'Teléfono / email del cliente' },
  { key: 'secondDate', label: 'Fecha de vencimiento / validez' },
  { key: 'reference', label: 'Referencia del documento' },
  { key: 'itemReference', label: 'Referencia de los artículos' },
  { key: 'paymentInfo', label: 'Datos de pago / IBAN' },
  { key: 'notes', label: 'Notas y observaciones' },
];

// The three configurable document types the owner sees as tabs.
export const DOC_TYPE_TABS = [
  { key: 'invoice', label: 'Factura' },
  { key: 'delivery_note', label: 'Albarán' },
  { key: 'estimate', label: 'Presupuesto' },
];

// Accepted payment methods printed at the end of the factura. Each method can
// require extra data (e.g. Bizum needs a phone number); `fields` lists the
// data inputs shown in Diseño when the method is enabled. The values are
// stored under design.payment_methods[key] = { enabled, ...field values }.
export const PAYMENT_METHODS = [
  { key: 'transferencia', label: 'Transferencia bancaria', fields: [
    { key: 'banco', label: 'Banco', placeholder: 'Banco Santander' },
    { key: 'iban', label: 'IBAN', placeholder: 'ES00 0000 0000 00 0000000000' },
    { key: 'swift', label: 'SWIFT / BIC', placeholder: 'BSCHESMMXXX' },
  ] },
  { key: 'bizum', label: 'Bizum', fields: [
    { key: 'telefono', label: 'Teléfono Bizum', placeholder: '600 000 000' },
  ] },
  { key: 'paypal', label: 'PayPal', fields: [
    { key: 'cuenta', label: 'Email / usuario PayPal', placeholder: 'pagos@empresa.com' },
  ] },
  { key: 'tarjeta', label: 'Tarjeta de crédito / débito', fields: [] },
  { key: 'efectivo', label: 'Efectivo', fields: [] },
];

const ALL_FIELDS_ON = Object.fromEntries(FIELD_DEFS.map((f) => [f.key, true]));

// The four DIRECTA tiles the till has always had. `key` is the keyboard
// shortcut (one letter; pressed with a price on the display it rings the line).
export function defaultDirectaTiles() {
  return [
    { code: 'V', name: 'VARIOS', tax_rate: 0, key: 'V' },
    { code: 'V2', name: 'VARIOS2', tax_rate: 0, key: '' },
    { code: 'F', name: 'FRUTA', tax_rate: 0, key: 'F' },
    { code: 'D', name: 'VERDURA', tax_rate: 0, key: 'D' },
  ];
}
export function normaliseDirectaTiles(stored) {
  if (!Array.isArray(stored) || stored.length === 0) return defaultDirectaTiles();
  const out = stored
    .filter((t) => t && typeof t === 'object' && String(t.name || '').trim())
    .map((t, i) => ({
      code: String(t.code || '').trim().toUpperCase().slice(0, 6) || `D${i + 1}`,
      name: String(t.name || '').trim().toUpperCase().slice(0, 30),
      tax_rate: Number.isFinite(Number(t.tax_rate)) ? Number(t.tax_rate) : 0,
      key: String(t.key || '').trim().slice(0, 1).toUpperCase(),
    }));
  return out.length ? out : defaultDirectaTiles();
}

export function defaultTypeDesign() {
  return { template: 'clasica', fields: { ...ALL_FIELDS_ON } };
}

// Empty payment-method config: every method disabled, all data fields blank.
function defaultPaymentMethods() {
  return Object.fromEntries(
    PAYMENT_METHODS.map((m) => [
      m.key,
      { enabled: false, ...Object.fromEntries(m.fields.map((fl) => [fl.key, ''])) },
    ]),
  );
}

export function defaultDesign() {
  return {
    primary_color: '#1a4f8b',
    font: 'Sans-serif',
    show_logo: true,
    watermark: true,
    logo_watermark: false,
    barcode: true,
    // Print per-line Precio / Importe with IVA included (gross) instead of net.
    prices_with_vat: false,
    // Print BOTH unit prices per line (Precio sin IVA | Precio con IVA) on
    // facturas/albaranes/presupuestos — for clients who reason in one or the
    // other. OFF by default: single Precio column as today.
    show_both_prices: false,
    // Show the "SubTotal" figure on the Artículos-Total strip.
    show_subtotal: true,
    // RYT: when an article carries a discount, print "Total sin descuento" and
    // "Descuento" above the SubTotal. Display only. OFF by default, so no
    // existing document changes until the account switches it on in Diseño.
    show_discount_summary: false,
    // Article-row type size (see ITEM_FONT_SIZES).
    item_font_scale: 'normal',
    // Factura/albarán/presupuesto editors: after adding/scanning an article,
    // skip the quantity/price stops and drop the cursor on the next article
    // line so the next one can be added straight away ("Configuración del
    // programa").
    focus_search_after_add: false,
    // TPV only: round a scanned article's gross (VAT-included) price to the
    // cent as it enters the ticket, so PRECIO × CANTIDAD always equals the
    // printed TOTAL exactly. OFF by default — legacy behavior carries the
    // full-precision gross (a net price like 0,46 + 21% = 0,5566 shows as
    // 0,56 but multiplies at 0,5566). Per-account opt-in ("Configuración del
    // programa"); does NOT touch the document/IBAE totals formula.
    tpv_round_unit_price: false,
    // TPV only: "Redondeo por artículo" — the ticket TOTAL is the sum of the
    // PVPs (gross line totals) and base/IVA are back-computed per rate (IVA
    // incluido method, lib/taxes.js retailTotals). Fixes tickets where PRECIO
    // × CANTIDAD reads 2,10 but the canonical net-driven total says 2,11.
    // Implies tpv_round_unit_price. OFF by default — the canonical formula is
    // untouched for accounts that don't opt in.
    tpv_gross_totals: false,
    // Facturas/presupuestos/albaranes: "Redondeo por artículo" for DOCUMENTS —
    // the article's VAT-included price is snapped to the cent as it enters a
    // line, and the TOTAL is the sum of those prices, with base/IVA
    // back-computed per rate (IVA incluido method, lib/taxes.js
    // documentGrossTotals). Fixes a catalogue price that cannot survive the
    // round trip through the stored net: 280,00 @ 18 % is kept as net
    // 237,2881, which re-grosses to 279,999958 — so 1.000 units invoiced as
    // 279.999,96 instead of 280.000,00 (EGAS, 12-09-2026). OFF by default; the
    // canonical net-driven formula is untouched for every account that leaves
    // it off.
    doc_gross_totals: false,
    // TPV only: scanning an article already on the ticket adds +1 to that
    // line (ON, legacy behavior) or opens a new line every time (OFF).
    tpv_group_lines: true,
    // Pagos: a payment recorded on the Pagos page against an invoice also
    // updates that invoice's importe pagado / pendiente (and marks it paid once
    // settled). Until an account opts in, the Pagos page only writes its own
    // ledger row, exactly as before, so no existing document's printed
    // paid/pending figures move. Pakistan gets this behaviour regardless — that
    // is where the missing sync was reported (see lib/invoicePayments.js).
    payments_update_invoice: false,
    // Spain and Pakistan: every payment-method picker (Pagos, Gastos, the
    // invoice "cobrado con" box) lists the country's banks and mobile wallets,
    // with a transaction-number box. ON by default — the owner asked for it
    // built in (Pakistan 12-09-2026, Spain 13-09-2026); an account switches it
    // off to get the short list back (see lib/bankList.js). The first day's
    // key, pk_bank_list, is still read below.
    bank_list: true,
    // Informes: "Mayor de clientes" / "Mayor de proveedores" — a statement per
    // customer (invoices issued + payments received) and per supplier (invoices
    // received + payments sent), with running balance and Excel/PDF export.
    // Built for Pakistan (always on there, lib/ledger.js) and OFF elsewhere
    // until 13-09-2026, when the owner asked for Spanish accounts to get the
    // same ledgers: ON by default now. It only adds two reports, and an
    // account switches it off in Configuración del programa; one that had
    // stored `false` stays off.
    ledger_reports: true,
    // Facturas: an issued factura / ticket can no longer be edited or deleted;
    // it is cancelled with a credit note (rectificativa) for the whole amount.
    // Built for Pakistan (always on there, lib/ledger.js) and OFF elsewhere
    // until 13-09-2026, when the owner asked for Spain to work the same way:
    // ON by default now, and an account that needs to keep editing issued
    // documents switches it off in Configuración del programa. An account
    // that had stored `false` stays off.
    lock_issued_invoices: true,
    // Facturas (Spain): the "Descargar factura Facturae (FACE)" action on an
    // issued invoice — the XML for invoicing public administrations through
    // FACe. Almost no account invoices an ayuntamiento, and the XML still
    // needs an XAdES signature before FACe accepts it, so the owner asked for
    // it out of the way (24-09-2026): OFF by default, the button only shows
    // once an account switches it on in Personalización › Configuración del
    // programa. The generator (lib/facturae.js) is untouched.
    facturae_download: false,
    // Clientes/Proveedores: warn before creating a contact whose tax id or name
    // already exists on the account, instead of silently adding a duplicate.
    // OFF by default so no existing workflow grows an extra prompt; Pakistan
    // gets it regardless (see lib/contactDedup.js).
    warn_duplicate_contacts: false,
    // Articulos: warn before saving an article whose barcode (primary, extra or
    // case code) is already used by another article ANYWHERE in the catalogue,
    // not just the section currently on screen. Two articles sharing a code make
    // a scan resolve to whichever row comes back first. OFF by default so no
    // existing workflow grows an extra prompt; Pakistan gets it regardless
    // (see lib/productDedup.js).
    warn_duplicate_barcodes: false,
    // "Precio mayorista / por paquete": an article can carry a second price
    // (products.alt_qty/alt_price) that takes over on its own once a line
    // reaches N units — at the till and on facturas/presupuestos/pedidos.
    // OFF by default: until an account opts in, every line prices off the
    // normal price, so no account's figures move under it. The two boxes only
    // show in the article form while this is ON (see lib/qtyBreak.js).
    wholesale_price: false,
    // "Precio y código de caja" (peti): an article can carry the price of a
    // whole CASE and the barcode printed on the carton
    // (products.pack_price/pack_units/pack_barcodes). Scanning a case code —
    // at the till or on facturas/presupuestos/pedidos — rings ONE case at the
    // case price instead of one unit. OFF by default: until an account opts
    // in, case codes are not even looked at and the boxes stay out of the
    // article form (see lib/packPrice.js).
    pack_price: false,
    // TPV only (zpos parity): "Inhabilitar cantidades negativas". OFF (default,
    // as on the zpos terminal): pressing − on a 1-unit line turns it into an
    // abono (−1, −2…) so returns can be rung up; ON: the line is removed at 0.
    tpv_disable_negative: false,
    // TPV only: decode balance labels (EAN prefix 20-29 with the price
    // embedded) and customer cards (999…) when a scan matches no article.
    tpv_scale_labels: true,
    // TPV only: the DIRECTA tiles (manual-price lines). Each tile: code shown
    // in the ARTÍCULO column, name, IVA rate and its single-letter shortcut.
    tpv_directa_tiles: defaultDirectaTiles(),
    // TPV only: fidelización. Points earned per `per_euros` of ticket; a redeem
    // tier of `redeem_points` is worth `redeem_euros` off the ticket.
    tpv_loyalty: { enabled: false, points: 1, per_euros: 1, redeem_points: 100, redeem_euros: 1 },
    // "Modo TPV (hardware)": the till prints tickets silently on the POS
    // printer, kicks the cash drawer and reads the weight scale through the
    // local zBilling TPV Bridge service (tpv-bridge/ on the till PC). OFF =
    // browser printing / blank-slip drawer kick, as before. The bridge URL and
    // the scale COM port are per-device (localStorage), not account settings.
    tpv_hardware: false,
    // Artículos only: enter/see the cost price with IVA included. The stored
    // products.cost_price is ALWAYS the net cost (margin and Informes compare
    // it against the net sale base), so this flag only grosses the figure up
    // for display and de-grosses what the user types before saving. OFF by
    // default — legacy behavior treats the typed cost as already net.
    cost_price_with_vat: false,
    // Artículos: "Tienda de móviles". Every handset of the same model is a
    // different unit with its own IMEI, so the article form grows an IMEI box
    // (one line per phone) and the catalogue search resolves an IMEI to its
    // article. OFF by default — no other account sees the field. The numbers
    // live in products.imeis; switching the toggle back off only hides the
    // box, it never clears what is stored.
    mobile_shop: false,
    // "Apariencia": the colour theme of the app shell for every user of the
    // account (top bar, buttons, links, tints — never the documents) and
    // whether the company logo sits next to the zBilling wordmark in the top
    // bar. See lib/uiTheme.js. Default = the classic zBilling look, logo off,
    // so no account changes until its owner picks something.
    ui_theme: DEFAULT_UI_THEME,
    header_logo: false,
    // "Apariencia": the company logo as a faint watermark behind every page
    // of the app, like the logo watermark on a factura. The default depends on
    // the account's country — ON in Pakistan (asked 14-09-2026), opt-in
    // elsewhere — so it is decided in mergeDesign(); `false` here is only the
    // country-less fallback. It only shows when a logo is uploaded.
    page_watermark: false,
    payment_methods: defaultPaymentMethods(),
    by_type: {
      invoice: defaultTypeDesign(),
      delivery_note: defaultTypeDesign(),
      estimate: defaultTypeDesign(),
    },
  };
}

// Normalise whatever is stored (possibly null / partial / from an older shape)
// into a complete design object with every field present.
// Coerce a stored flag to a real boolean. JSONB normally stores booleans, but a
// value that ever ended up as the string "false"/"0" must not read as true.
const toBool = (v, fallback) => {
  if (v === undefined || v === null) return fallback;
  if (typeof v === 'string') return v !== 'false' && v !== '0' && v !== '';
  return !!v;
};

export function mergeDesign(stored, { countryCode } = {}) {
  const d = defaultDesign();
  // Page watermark: on by default for a Pakistani account, opt-in everywhere
  // else (so no Spanish account changes until its owner switches it on); a
  // stored value always wins. Callers that don't pass the country get "off".
  d.page_watermark = countryCode === 'PK';
  if (!stored || typeof stored !== 'object') return d;
  const out = {
    primary_color: stored.primary_color || d.primary_color,
    font: stored.font || d.font,
    show_logo: toBool(stored.show_logo, d.show_logo),
    watermark: toBool(stored.watermark, d.watermark),
    logo_watermark: toBool(stored.logo_watermark, d.logo_watermark),
    barcode: toBool(stored.barcode, d.barcode),
    prices_with_vat: toBool(stored.prices_with_vat, d.prices_with_vat),
    show_both_prices: toBool(stored.show_both_prices, d.show_both_prices),
    show_subtotal: toBool(stored.show_subtotal, d.show_subtotal),
    show_discount_summary: toBool(stored.show_discount_summary, d.show_discount_summary),
    item_font_scale: ITEM_FONT_SIZES.some((s) => s.key === stored.item_font_scale)
      ? stored.item_font_scale : d.item_font_scale,
    focus_search_after_add: toBool(stored.focus_search_after_add, d.focus_search_after_add),
    tpv_round_unit_price: toBool(stored.tpv_round_unit_price, d.tpv_round_unit_price),
    tpv_gross_totals: toBool(stored.tpv_gross_totals, d.tpv_gross_totals),
    doc_gross_totals: toBool(stored.doc_gross_totals, d.doc_gross_totals),
    tpv_group_lines: toBool(stored.tpv_group_lines, d.tpv_group_lines),
    payments_update_invoice: toBool(stored.payments_update_invoice, d.payments_update_invoice),
    bank_list: toBool(stored.bank_list, toBool(stored.pk_bank_list, d.bank_list)),
    ledger_reports: toBool(stored.ledger_reports, d.ledger_reports),
    lock_issued_invoices: toBool(stored.lock_issued_invoices, d.lock_issued_invoices),
    facturae_download: toBool(stored.facturae_download, d.facturae_download),
    warn_duplicate_contacts: toBool(stored.warn_duplicate_contacts, d.warn_duplicate_contacts),
    warn_duplicate_barcodes: toBool(stored.warn_duplicate_barcodes, d.warn_duplicate_barcodes),
    wholesale_price: toBool(stored.wholesale_price, d.wholesale_price),
    pack_price: toBool(stored.pack_price, d.pack_price),
    cost_price_with_vat: toBool(stored.cost_price_with_vat, d.cost_price_with_vat),
    mobile_shop: toBool(stored.mobile_shop, d.mobile_shop),
    ui_theme: isUiTheme(stored.ui_theme) ? stored.ui_theme : d.ui_theme,
    header_logo: toBool(stored.header_logo, d.header_logo),
    page_watermark: toBool(stored.page_watermark, d.page_watermark),
    tpv_disable_negative: toBool(stored.tpv_disable_negative, d.tpv_disable_negative),
    tpv_scale_labels: toBool(stored.tpv_scale_labels, d.tpv_scale_labels),
    tpv_hardware: toBool(stored.tpv_hardware, d.tpv_hardware),
    tpv_directa_tiles: normaliseDirectaTiles(stored.tpv_directa_tiles),
    tpv_loyalty: {
      enabled: toBool(stored.tpv_loyalty?.enabled, d.tpv_loyalty.enabled),
      points: Number(stored.tpv_loyalty?.points) > 0 ? Number(stored.tpv_loyalty.points) : d.tpv_loyalty.points,
      per_euros: Number(stored.tpv_loyalty?.per_euros) > 0 ? Number(stored.tpv_loyalty.per_euros) : d.tpv_loyalty.per_euros,
      redeem_points: Number(stored.tpv_loyalty?.redeem_points) > 0 ? Number(stored.tpv_loyalty.redeem_points) : d.tpv_loyalty.redeem_points,
      redeem_euros: Number(stored.tpv_loyalty?.redeem_euros) > 0 ? Number(stored.tpv_loyalty.redeem_euros) : d.tpv_loyalty.redeem_euros,
    },
    payment_methods: {},
    by_type: {},
  };
  // Merge each known payment method, keeping stored enabled flag + field values.
  for (const m of PAYMENT_METHODS) {
    const s = stored.payment_methods?.[m.key] || {};
    out.payment_methods[m.key] = {
      enabled: s.enabled !== undefined ? !!s.enabled : d.payment_methods[m.key].enabled,
      ...Object.fromEntries(m.fields.map((fl) => [fl.key, s[fl.key] ?? ''])),
    };
  }
  for (const key of Object.keys(d.by_type)) {
    const s = stored.by_type?.[key] || {};
    out.by_type[key] = {
      template: TEMPLATES.some((t) => t.id === s.template) ? s.template : d.by_type[key].template,
      fields: { ...ALL_FIELDS_ON, ...(s.fields || {}) },
    };
  }
  return out;
}

// Proforma / simplified tickets reuse the Factura config; albarán and
// presupuesto have their own.
export function typeConfigKey(docKey) {
  if (docKey === 'delivery_note') return 'delivery_note';
  if (docKey === 'estimate') return 'estimate';
  return 'invoice';
}

// Resolve the design for a specific document, returning { global, type }.
export function getTypeDesign(design, docKey) {
  const merged = mergeDesign(design);
  return { global: merged, type: merged.by_type[typeConfigKey(docKey)] };
}
